import { useState, useEffect, useRef, useCallback } from 'react'
import { ArrowLeft, Plus, X, RefreshCw, Trash2, StickyNote, BookOpen, Check, Flag } from 'lucide-react'
import { clientProjectsApi } from '@/api/client'
import type { ClientProject, ClientProjectDetail, ClientProjectPhaseItem, ProjectProgressEntry } from '@/api/client'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'

// ── Stages ────────────────────────────────────────────────────────────────────

const STAGES = [
  'Awaiting Allocation',
  'Planning / Req / Design',
  'Waiting for Partner Forms',
  'Waiting for Customer',
  'Waiting for Frankie Internal',
  'Setup and Config',
  'Build and Test (UAT)',
  'Production Ready (PVT)',
  'Go Live / Hypercare',
  'Transitioned to Support',
] as const

// ── Status definitions ────────────────────────────────────────────────────────

const COLOR_MAP: Record<string, { dot: string; bg: string; text: string }> = {
  gray:   { dot: '#9ca3af', bg: 'rgba(156,163,175,0.15)', text: '#9ca3af' },
  amber:  { dot: '#f59e0b', bg: 'rgba(245,158,11,0.15)',  text: '#d97706' },
  blue:   { dot: '#3b82f6', bg: 'rgba(59,130,246,0.15)',  text: '#3b82f6' },
  green:  { dot: '#22c55e', bg: 'rgba(34,197,94,0.15)',   text: '#16a34a' },
  purple: { dot: '#a855f7', bg: 'rgba(168,85,247,0.15)',  text: '#a855f7' },
}

interface StatusOption {
  value: string
  label: string
  color: string
  terminal?: boolean
  na?: boolean
}

const STATUS_OPTIONS: Record<string, StatusOption[]> = {
  presales_handover: [
    { value: 'not_started',  label: 'Not started', color: 'gray' },
    { value: 'received',     label: 'Received',    color: 'blue' },
    { value: 'reviewed',     label: 'Reviewed',    color: 'amber' },
    { value: 'completed',    label: 'Completed',   color: 'green', terminal: true },
    { value: 'na',           label: 'N/A',         color: 'gray',  na: true },
  ],
  workflow_config: [
    { value: 'not_started',          label: 'Not started',          color: 'gray' },
    { value: 'clarifying_with_client', label: 'Clarifying w/ client', color: 'amber' },
    { value: 'jira_ticket_submitted', label: 'Jira ticket submitted', color: 'blue' },
    { value: 'configuring_in_uat',   label: 'Configuring in UAT',   color: 'blue' },
    { value: 'client_reviewing',     label: 'Client reviewing',     color: 'purple' },
    { value: 'completed',            label: 'Completed',            color: 'green', terminal: true },
    { value: 'na',                   label: 'N/A',                  color: 'gray',  na: true },
  ],
}

const GENERIC_OPTIONS: StatusOption[] = [
  { value: 'not_started', label: 'Not started', color: 'gray' },
  { value: 'in_progress', label: 'In progress', color: 'amber' },
  { value: 'blocked',     label: 'Blocked',     color: 'purple' },
  { value: 'completed',   label: 'Completed',   color: 'green', terminal: true },
  { value: 'na',          label: 'N/A',         color: 'gray',  na: true },
]

const SYNTHETIC_LABELS: Record<string, { label: string; color: string; terminal?: boolean }> = {
  sent:               { label: 'Sent',                color: 'green', terminal: true },
  scheduled:          { label: 'Scheduled',           color: 'blue' },
  prepared:           { label: 'Prepared',            color: 'amber' },
  triaging:           { label: 'Triaging',            color: 'amber' },
  client_accessed:    { label: 'Client accessed',     color: 'amber' },
  configuring_in_uat: { label: 'Configuring in UAT',  color: 'blue' },
  dvs_processing:     { label: 'DVS processing',      color: 'amber' },
  client_submitted:   { label: 'Client submitted',    color: 'amber' },
  form_sent_to_client:{ label: 'Form sent to client', color: 'blue' },
  sent_to_client:     { label: 'Sent to client',      color: 'blue' },
  stakeholders_invited:{ label: 'Stakeholders invited', color: 'green', terminal: true },
  not_confirmed:      { label: 'Not confirmed',       color: 'gray' },
  not_sent:           { label: 'Not sent',            color: 'gray' },
  not_verified:       { label: 'Not verified',        color: 'gray' },
}

function statusOptionsFor(itemType: string): StatusOption[] {
  return STATUS_OPTIONS[itemType] ?? GENERIC_OPTIONS
}

function statusMeta(value: string, itemType: string): StatusOption {
  const opts = statusOptionsFor(itemType)
  const found = opts.find((o) => o.value === value)
  if (found) return found
  const syn = SYNTHETIC_LABELS[value]
  if (syn) return { value, ...syn }
  return { value, label: value, color: 'gray' }
}

function countItems(items: ClientProjectPhaseItem[]): { done: number; total: number } {
  let done = 0, total = 0
  for (const it of items) {
    if (it.subitems?.length) {
      // parent with subitems: count subitems only
      const sub = countItems(it.subitems)
      done += sub.done; total += sub.total
    } else {
      const m = statusMeta(it.status, it.type)
      total++
      if (m.terminal || m.na) done++
    }
  }
  return { done, total }
}

function phaseProgress(items: ClientProjectPhaseItem[]) {
  const { done, total } = countItems(items)
  return { done, total, pct: total === 0 ? 0 : Math.round((done / total) * 100) }
}

function overallProgress(detail: ClientProjectDetail) {
  let done = 0, total = 0
  for (const p of detail.phases) {
    const pr = phaseProgress(p.items)
    done += pr.done
    total += pr.total
  }
  return { done, total, pct: total === 0 ? 0 : Math.round((done / total) * 100) }
}

// ── Mini components ───────────────────────────────────────────────────────────

function StatusDot({ status, itemType }: { status: string; itemType: string }) {
  const m = statusMeta(status, itemType)
  const c = COLOR_MAP[m.color] ?? COLOR_MAP.gray
  return (
    <span
      style={{ width: 8, height: 8, borderRadius: 999, background: c.dot, flexShrink: 0 }}
      className="inline-block"
    />
  )
}

function StatusPill({ status, itemType }: { status: string; itemType: string }) {
  const m = statusMeta(status, itemType)
  const c = COLOR_MAP[m.color] ?? COLOR_MAP.gray
  return (
    <span
      style={{
        background: c.bg, color: c.text,
        fontSize: 10.5, fontWeight: 600,
        padding: '1px 7px', borderRadius: 999,
        whiteSpace: 'nowrap', lineHeight: '18px',
      }}
    >
      {m.label}
    </span>
  )
}

function MiniProgress({ done, total, pct }: { done: number; total: number; pct: number }) {
  const color = pct === 100 ? '#22c55e' : pct > 0 ? '#3b82f6' : '#d1d5db'
  return (
    <div className="flex items-center gap-2" style={{ minWidth: 80 }}>
      <div style={{ flex: 1, height: 4, borderRadius: 2, background: 'rgba(156,163,175,0.2)', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 2, transition: 'width 0.3s' }} />
      </div>
      <span style={{ fontSize: 11, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums', minWidth: 28, textAlign: 'right' }}>
        {done}/{total}
      </span>
    </div>
  )
}

function relTime(iso: string | null): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  const now = Date.now()
  const diff = now - then
  const day = 86400000
  if (diff < 0) return 'future'
  if (diff < day) return 'today'
  if (diff < 2 * day) return 'yesterday'
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`
  if (diff < 30 * day) return `${Math.floor(diff / (7 * day))}w ago`
  return `${Math.floor(diff / (30 * day))}mo ago`
}

function duration(iso: string | null | undefined): string {
  if (!iso) return ''
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 0) return ''
  const days = Math.floor(ms / 86400000)
  const hours = Math.floor(ms / 3600000)
  if (days > 0) return `${days}d`
  if (hours > 0) return `${hours}h`
  return '<1h'
}

const BLOCKED_BY_OPTIONS = [
  { value: 'us',      label: 'Us' },
  { value: 'client',  label: 'Client' },
  { value: 'product', label: 'Product' },
  { value: 'other',   label: 'Other' },
]

function daysInStage(stageEnteredAt: string): number {
  return Math.floor((Date.now() - new Date(stageEnteredAt).getTime()) / 86400000)
}

function stageBadgeStyle(stage: string): React.CSSProperties {
  const map: Record<string, { bg: string; color: string }> = {
    'Awaiting Allocation':       { bg: 'rgba(156,163,175,0.15)', color: '#9ca3af' },
    'Planning / Req / Design':   { bg: 'rgba(59,130,246,0.15)',  color: '#3b82f6' },
    'Waiting for Partner Forms':  { bg: 'rgba(245,158,11,0.15)',  color: '#d97706' },
    'Waiting for Customer':       { bg: 'rgba(245,158,11,0.15)',  color: '#d97706' },
    'Waiting for Frankie Internal':{ bg: 'rgba(245,158,11,0.15)', color: '#d97706' },
    'Setup and Config':           { bg: 'rgba(59,130,246,0.15)',  color: '#3b82f6' },
    'Build and Test (UAT)':       { bg: 'rgba(168,85,247,0.15)',  color: '#a855f7' },
    'Production Ready (PVT)':     { bg: 'rgba(34,197,94,0.15)',   color: '#16a34a' },
    'Go Live / Hypercare':        { bg: 'rgba(16,185,129,0.15)',  color: '#10b981' },
    'Transitioned to Support':    { bg: 'rgba(99,102,241,0.15)',  color: '#6366f1' },
  }
  const s = map[stage] ?? { bg: 'rgba(156,163,175,0.15)', color: '#9ca3af' }
  return { background: s.bg, color: s.color, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' }
}

function Checkbox({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      onClick={onChange}
      style={{
        width: 15, height: 15, flexShrink: 0,
        borderRadius: 3,
        border: checked ? 'none' : '1.5px solid var(--border-color)',
        background: checked ? '#22c55e' : 'transparent',
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 0, transition: 'all 0.1s',
      }}
    >
      {checked && (
        <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
          <path d="M1 3.5L3.5 6L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  )
}

// Derive a parent item's status from its sub-items
function deriveParentStatus(subitems: ClientProjectPhaseItem[]): { status: string; color: string; label: string } {
  if (!subitems.length) return { status: 'not_started', color: 'gray', label: 'Not started' }
  let done = 0
  for (const s of subitems) {
    const m = statusMeta(s.status, s.type)
    if (m.terminal || m.na) done++
  }
  if (done === 0) return { status: 'not_started', color: 'gray', label: 'Not started' }
  if (done === subitems.length) return { status: 'completed', color: 'green', label: 'Completed' }
  return { status: 'in_progress', color: 'amber', label: `${done}/${subitems.length} done` }
}

// ── Blocked-by badge ─────────────────────────────────────────────────────────

function BlockedByBadge({
  item,
  onUpdate,
}: {
  item: ClientProjectPhaseItem
  onUpdate: (itemId: number, blockedBy: string | null) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const label = item.blockedBy
    ? item.blockedBy.charAt(0).toUpperCase() + item.blockedBy.slice(1)
    : '?'

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v) }}
        style={{
          background: item.blockedBy ? 'rgba(168,85,247,0.12)' : 'rgba(168,85,247,0.06)',
          border: '1px solid rgba(168,85,247,0.3)',
          borderRadius: 4,
          padding: '1px 6px',
          fontSize: 10,
          color: '#a855f7',
          cursor: 'pointer',
          lineHeight: '16px',
        }}
      >
        by {label} ▾
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 60,
          background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
          borderRadius: 6, padding: 4, minWidth: 110,
          boxShadow: '0 4px 12px rgba(0,0,0,0.25)', marginTop: 2,
        }}>
          {BLOCKED_BY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={(e) => { e.stopPropagation(); onUpdate(item.id, opt.value); setOpen(false) }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '4px 8px',
                background: item.blockedBy === opt.value ? 'rgba(168,85,247,0.12)' : 'transparent',
                border: 'none', borderRadius: 4, cursor: 'pointer',
                fontSize: 12, color: item.blockedBy === opt.value ? '#a855f7' : 'var(--text-primary)',
              }}
              onMouseEnter={(e) => { if (item.blockedBy !== opt.value) e.currentTarget.style.background = 'var(--bg-primary)' }}
              onMouseLeave={(e) => { if (item.blockedBy !== opt.value) e.currentTarget.style.background = 'transparent' }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Item panel modal (notes / tutorial) ───────────────────────────────────────

function ItemPanelModal({
  item,
  mode,
  canEdit,
  onClose,
  onSave,
}: {
  item: ClientProjectPhaseItem
  mode: 'notes' | 'tutorial'
  canEdit: boolean
  onClose: () => void
  onSave: (value: string) => Promise<void>
}) {
  const initial = mode === 'notes' ? (item.notes ?? '') : (item.tutorial ?? '')
  const [value, setValue] = useState(initial)
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    await onSave(value)
    setSaving(false)
    onClose()
  }

  const accentColor = mode === 'notes' ? '#f59e0b' : '#3b82f6'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-4 py-3 border-b border-[var(--border-color)]">
          <div>
            <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: accentColor }}>
              {mode === 'notes' ? 'Notes' : 'Tutorial'}
            </span>
            <p className="text-sm font-medium text-[var(--text-primary)] mt-0.5">{item.title}</p>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--text-muted)] hover:text-[var(--text-primary)] bg-transparent border-none cursor-pointer p-1 mt-0.5"
          >
            <X size={14} />
          </button>
        </div>
        <div className="p-4">
          {canEdit ? (
            <textarea
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={
                mode === 'notes'
                  ? 'Add notes for this step…'
                  : 'Add tutorial guidance — what does someone need to know to complete this step?'
              }
              rows={6}
              className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg p-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 resize-none"
              style={{ '--tw-ring-color': accentColor } as React.CSSProperties}
            />
          ) : (
            <div className="text-sm text-[var(--text-primary)] whitespace-pre-wrap leading-relaxed min-h-[80px]">
              {value || <span className="text-[var(--text-muted)] italic">No tutorial added yet.</span>}
            </div>
          )}
        </div>
        {canEdit && (
          <div className="flex justify-end gap-2 px-4 pb-4">
            <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Status dropdown ───────────────────────────────────────────────────────────

function StatusDropdown({
  item,
  onUpdate,
  onClose,
  anchorRef,
}: {
  item: ClientProjectPhaseItem
  onUpdate: (itemId: number, status: string) => void
  onClose: () => void
  anchorRef: React.RefObject<HTMLElement | null>
}) {
  const opts = statusOptionsFor(item.type)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node) &&
          anchorRef.current && !anchorRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose, anchorRef])

  return (
    <div
      ref={ref}
      style={{
        position: 'absolute', zIndex: 50, top: '100%', right: 0,
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border-color)',
        borderRadius: 8, padding: 4, minWidth: 180,
        boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
      }}
    >
      {opts.map((opt) => {
        const c = COLOR_MAP[opt.color] ?? COLOR_MAP.gray
        const isActive = item.status === opt.value
        return (
          <button
            key={opt.value}
            onClick={() => { onUpdate(item.id, opt.value); onClose() }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              width: '100%', padding: '5px 8px',
              background: isActive ? c.bg : 'transparent',
              border: 'none', borderRadius: 5, cursor: 'pointer',
              fontSize: 12.5, color: 'var(--text-primary)', textAlign: 'left',
            }}
            onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'var(--bg-primary)' }}
            onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent' }}
          >
            <span style={{ width: 7, height: 7, borderRadius: 999, background: c.dot, flexShrink: 0 }} />
            <span style={{ flex: 1 }}>{opt.label}</span>
            {isActive && <span style={{ fontSize: 11, color: c.text }}>✓</span>}
          </button>
        )
      })}
    </div>
  )
}

// ── New project modal ─────────────────────────────────────────────────────────

function NewProjectModal({
  onClose,
  onCreate,
}: {
  onClose: () => void
  onCreate: (project: ClientProject) => void
}) {
  const [form, setForm] = useState({ customerName: '', owner: '', region: 'AU', targetGoLive: '' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.customerName.trim() || !form.owner.trim()) {
      setError('Customer name and owner are required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const res = await clientProjectsApi.create({
        customerName: form.customerName.trim(),
        owner: form.owner.trim(),
        region: form.region || 'AU',
        targetGoLive: form.targetGoLive || undefined,
      })
      if (!res.success || !res.data) {
        setError(res.error ?? 'Failed to create project')
        return
      }
      // fetch the new project
      const detail = await clientProjectsApi.get(res.data.id)
      if (detail.success && detail.data) {
        onCreate(detail.data)
        onClose()
      } else {
        setError(detail.error ?? 'Created but could not load project')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-color)]">
          <h2 className="text-sm font-semibold text-[var(--text-primary)]">New Client Project</h2>
          <button
            onClick={onClose}
            className="text-[var(--text-muted)] hover:text-[var(--text-primary)] bg-transparent border-none cursor-pointer text-lg leading-none p-1"
          >
            <X size={16} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
          <div className="space-y-1">
            <label className="text-xs font-medium text-[var(--text-muted)]">Customer Name *</label>
            <input
              autoFocus
              value={form.customerName}
              onChange={(e) => setForm((f) => ({ ...f, customerName: e.target.value }))}
              placeholder="e.g. Acme Corp"
              className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-[var(--text-muted)]">Owner *</label>
            <input
              value={form.owner}
              onChange={(e) => setForm((f) => ({ ...f, owner: e.target.value }))}
              placeholder="e.g. Priya Shah"
              className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-[var(--text-muted)]">Region</label>
              <select
                value={form.region}
                onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))}
                className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {['AU', 'NZ', 'UK', 'SG', 'US', 'CA', 'Other'].map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-[var(--text-muted)]">Target Go-Live</label>
              <input
                type="date"
                value={form.targetGoLive}
                onChange={(e) => setForm((f) => ({ ...f, targetGoLive: e.target.value }))}
                className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary" size="sm" disabled={saving}>
              {saving ? 'Creating…' : 'Create Project'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── AddItemInput ──────────────────────────────────────────────────────────────

function AddItemInput({ placeholder, value, onChange, onConfirm, onCancel, indent }: {
  placeholder: string; value: string; onChange: (v: string) => void
  onConfirm: () => void; onCancel: () => void; indent: number
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: `4px 14px 4px ${indent}px`, borderBottom: '1px solid var(--bg-primary)' }}>
      <textarea
        autoFocus
        rows={2}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.shiftKey || e.metaKey)) { e.preventDefault(); onConfirm() }
          if (e.key === 'Escape') onCancel()
        }}
        placeholder={`${placeholder} (Shift+Enter to save)`}
        style={{
          flex: 1, background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
          borderRadius: 4, padding: '3px 7px', fontSize: 12.5, color: 'var(--text-primary)',
          outline: 'none', resize: 'vertical', minHeight: 38, lineHeight: 1.5,
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 2 }}>
        <button onClick={onConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#22c55e', padding: '1px 4px' }}>Add</button>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--text-muted)', padding: '1px 4px' }}>Cancel</button>
      </div>
    </div>
  )
}

// ── Stage stepper ─────────────────────────────────────────────────────────────

function StageStepper({ stage, projectId, onStageChange }: {
  stage: string
  projectId: number
  onStageChange: (newStage: string) => void
}) {
  const currentIdx = STAGES.indexOf(stage as typeof STAGES[number])
  const [updating, setUpdating] = useState(false)

  const handleClick = async (s: string) => {
    if (s === stage || updating) return
    setUpdating(true)
    try {
      const res = await clientProjectsApi.update(projectId, { stage: s })
      if (res.success) onStageChange(s)
    } finally {
      setUpdating(false)
    }
  }

  return (
    <div className="flex items-center gap-0 overflow-x-auto scrollbar-none">
      {STAGES.map((s, i) => {
        const done = i < currentIdx
        const active = i === currentIdx
        const dotColor = done ? '#22c55e' : active ? '#3b82f6' : 'var(--border-color)'
        const textColor = done ? '#22c55e' : active ? '#3b82f6' : 'var(--text-muted)'
        return (
          <div key={s} className="flex items-center" style={{ flexShrink: 0 }}>
            <button
              onClick={() => handleClick(s)}
              disabled={updating}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, background: 'none', border: 'none', cursor: updating ? 'default' : 'pointer', padding: '4px 6px' }}
            >
              <span style={{
                width: 10, height: 10, borderRadius: 999,
                background: dotColor,
                flexShrink: 0,
                boxShadow: active ? `0 0 0 3px rgba(59,130,246,0.2)` : 'none',
              }} />
              <span style={{ fontSize: 10, color: textColor, fontWeight: active ? 600 : 400, whiteSpace: 'nowrap', maxWidth: 80, textAlign: 'center', lineHeight: 1.3 }}>
                {s}
              </span>
            </button>
            {i < STAGES.length - 1 && (
              <div style={{ width: 24, height: 1, background: i < currentIdx ? '#22c55e' : 'var(--border-color)', flexShrink: 0, marginBottom: 16 }} />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── Detail view ───────────────────────────────────────────────────────────────

function ProjectDetail({
  project: initialProject,
  onBack,
  onProjectUpdated,
  onDelete,
}: {
  project: ClientProjectDetail
  onBack: () => void
  onProjectUpdated: (p: ClientProjectDetail) => void
  onDelete: (id: number) => void
}) {
  const [project, setProject] = useState(initialProject)
  const [openItem, setOpenItem] = useState<number | null>(null)
  const [itemRefs] = useState<Map<number, React.RefObject<HTMLDivElement | null>>>(() => new Map())
  const [hideComplete, setHideComplete] = useState(false)
  const [loading, setLoading] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [itemPanel, setItemPanel] = useState<{ id: number; mode: 'notes' | 'tutorial' } | null>(null)

  const handleDelete = async () => {
    setDeleting(true)
    onDelete(project.id)
    setDeleting(false)
  }

  const getItemRef = (id: number) => {
    if (!itemRefs.has(id)) itemRefs.set(id, { current: null })
    return itemRefs.get(id)!
  }

  const [editMode, setEditMode] = useState(false)
  const [renamingId, setRenamingId] = useState<number | 'phase' | null>(null)
  const [renamingPhaseN, setRenamingPhaseN] = useState<number | null>(null)
  const [addingItemPhase, setAddingItemPhase] = useState<{ n: number; title: string; parentId?: number } | null>(null)
  const [addingItemValue, setAddingItemValue] = useState('')
  const [addingPhase, setAddingPhase] = useState(false)
  const [addingPhaseValue, setAddingPhaseValue] = useState('')

  const refresh = useCallback(async () => {
    setLoading(true)
    const res = await clientProjectsApi.get(project.id)
    if (res.success && res.data) { setProject(res.data); onProjectUpdated(res.data) }
    setLoading(false)
  }, [project.id, onProjectUpdated])

  // Deep-update an item (including inside subitems)
  const patchItem = (phases: typeof project.phases, itemId: number, patch: Partial<ClientProjectPhaseItem>) =>
    phases.map((ph) => ({
      ...ph,
      items: ph.items.map((it) => {
        if (it.id === itemId) return { ...it, ...patch }
        if (it.subitems?.length) return { ...it, subitems: it.subitems.map((s) => s.id === itemId ? { ...s, ...patch } : s) }
        return it
      }),
    }))

  const removeItem = (phases: typeof project.phases, itemId: number) =>
    phases.map((ph) => ({
      ...ph,
      items: ph.items
        .filter((it) => it.id !== itemId)
        .map((it) => ({ ...it, subitems: it.subitems?.filter((s) => s.id !== itemId) })),
    })).filter((ph) => ph.items.length > 0)

  const handleStatusUpdate = async (itemId: number, status: string) => {
    await clientProjectsApi.updateItem(itemId, { status })
    const now = new Date().toISOString()
    const patch: Partial<ClientProjectPhaseItem> = { status, updatedAt: now }
    if (status !== 'blocked') { patch.blockedSince = null; patch.blockedBy = null }
    setProject((prev) => ({ ...prev, phases: patchItem(prev.phases, itemId, patch) }))
  }

  const handleUpdateBlockedBy = async (itemId: number, blockedBy: string | null) => {
    await clientProjectsApi.updateItem(itemId, { blockedBy })
    setProject((prev) => ({ ...prev, phases: patchItem(prev.phases, itemId, { blockedBy }) }))
  }

  const handleSaveNotes = async (itemId: number, notes: string) => {
    await clientProjectsApi.updateItem(itemId, { notes: notes || null })
    setProject((prev) => ({ ...prev, phases: patchItem(prev.phases, itemId, { notes: notes || null, updatedAt: new Date().toISOString() }) }))
  }

  const handleSaveTutorial = async (itemId: number, tutorial: string) => {
    await clientProjectsApi.updateItem(itemId, { tutorial: tutorial || null })
    setProject((prev) => ({ ...prev, phases: patchItem(prev.phases, itemId, { tutorial: tutorial || null }) }))
  }

  const handleRename = async (itemId: number, title: string) => {
    if (!title.trim()) return
    await clientProjectsApi.updateItem(itemId, { title: title.trim() })
    setProject((prev) => ({ ...prev, phases: patchItem(prev.phases, itemId, { title: title.trim() }) }))
    setRenamingId(null)
  }

  const handleRenamePhase = async (phaseN: number, title: string) => {
    if (!title.trim()) return
    await clientProjectsApi.renamePhase(project.id, phaseN, title.trim())
    setProject((prev) => ({
      ...prev,
      phases: prev.phases.map((ph) => ph.n === phaseN ? { ...ph, title: title.trim() } : ph),
    }))
    setRenamingPhaseN(null)
  }

  const handleDeleteItem = async (itemId: number) => {
    await clientProjectsApi.deleteItem(itemId)
    setProject((prev) => ({ ...prev, phases: removeItem(prev.phases, itemId) }))
  }

  const handleDeletePhase = async (phaseN: number) => {
    await clientProjectsApi.deletePhase(project.id, phaseN)
    setProject((prev) => ({ ...prev, phases: prev.phases.filter((ph) => ph.n !== phaseN) }))
  }

  const handleAddItem = async () => {
    if (!addingItemPhase || !addingItemValue.trim()) return
    const res = await clientProjectsApi.addItem(project.id, {
      phaseNumber: addingItemPhase.n,
      phaseTitle: addingItemPhase.title,
      title: addingItemValue.trim(),
      parentItemId: addingItemPhase.parentId,
    })
    setAddingItemPhase(null); setAddingItemValue('')
    if (res.success) await refresh()
  }

  const handleAddPhase = async () => {
    if (!addingPhaseValue.trim()) return
    await clientProjectsApi.addPhase(project.id, addingPhaseValue.trim())
    setAddingPhase(false); setAddingPhaseValue('')
    await refresh()
  }

  const handleStageChange = (newStage: string) => {
    setProject((prev) => ({ ...prev, stage: newStage, stageEnteredAt: new Date().toISOString() }))
  }

  const overall = overallProgress(project)

  // Render a single item row (used for both top-level items and subitems)
  const renderItem = (item: ClientProjectPhaseItem, phase: typeof project.phases[number], isSubitem = false) => {
    const hasSubitems = !isSubitem && (item.subitems?.length ?? 0) > 0
    const isOpen = openItem === item.id
    const ref = getItemRef(item.id)
    const isRenaming = renamingId === item.id
    const itemMeta = statusMeta(item.status, item.type)
    const isDone = !!(itemMeta.terminal || itemMeta.na)

    // Subitems: treat as checkbox — hide when "hide done" is on and checked
    if (isSubitem && hideComplete && isDone && !editMode) return null
    // Parent with no subitems: hide if done and hiding
    if (!isSubitem && !hasSubitems && hideComplete && isDone && !editMode) return null

    // Derived status for parent items that have subitems
    const derived = hasSubitems ? deriveParentStatus(item.subitems!) : null
    const derivedC = derived ? (COLOR_MAP[derived.color] ?? COLOR_MAP.gray) : null

    // Checkbox checked = completed or na
    const checkboxChecked = isDone

    const titleStyle: React.CSSProperties = {
      color: isSubitem && checkboxChecked ? '#16a34a' : 'var(--text-primary)',
      flex: 1,
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    }

    return (
      <div key={item.id}>
        <div
          className="group/item"
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: `0 14px 0 ${isSubitem ? 36 : 14}px`,
            height: isSubitem ? 26 : 30,
            borderBottom: '1px solid var(--bg-primary)',
            fontSize: isSubitem ? 12 : 12.5, position: 'relative',
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-primary)' }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = '' }}
        >
          {/* Left indicator */}
          {isSubitem ? (
            <Checkbox
              checked={checkboxChecked}
              onChange={() => handleStatusUpdate(item.id, checkboxChecked ? 'not_started' : 'completed')}
            />
          ) : (
            <StatusDot
              status={derived ? derived.status : item.status}
              itemType={derived ? 'generic' : item.type}
            />
          )}

          {/* Title */}
          {isRenaming ? (
            <input
              autoFocus
              defaultValue={item.title}
              onBlur={(e) => handleRename(item.id, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRename(item.id, e.currentTarget.value)
                if (e.key === 'Escape') setRenamingId(null)
              }}
              style={{
                flex: 1, background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                borderRadius: 4, padding: '1px 6px', fontSize: 'inherit', color: 'var(--text-primary)',
                outline: 'none',
              }}
            />
          ) : (
            <span
              style={titleStyle}
              onDoubleClick={editMode ? () => { setRenamingId(item.id) } : undefined}
            >
              {item.title}
            </span>
          )}

          {/* Right side */}
          {editMode ? (
            <div className="flex items-center gap-1 opacity-0 group-hover/item:opacity-100 transition-opacity">
              <button
                onClick={(e) => { e.stopPropagation(); setItemPanel({ id: item.id, mode: 'notes' }) }}
                title={item.notes ? 'View/edit notes' : 'Add notes'}
                style={{ background: 'none', border: 'none', padding: '1px 3px', cursor: 'pointer', borderRadius: 3, lineHeight: 0, color: item.notes ? '#f59e0b' : 'var(--text-muted)' }}
              >
                <StickyNote size={10} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setItemPanel({ id: item.id, mode: 'tutorial' }) }}
                title={item.tutorial ? 'View/edit tutorial' : 'Add tutorial'}
                style={{ background: 'none', border: 'none', padding: '1px 3px', cursor: 'pointer', borderRadius: 3, lineHeight: 0, color: item.tutorial ? '#3b82f6' : 'var(--text-muted)' }}
              >
                <BookOpen size={10} />
              </button>
              <button onClick={() => { setRenamingId(item.id) }}
                style={{ background: 'none', border: 'none', padding: '1px 4px', cursor: 'pointer', fontSize: 10, color: 'var(--text-muted)', borderRadius: 3 }}>
                Rename
              </button>
              {!isSubitem && (
                <button onClick={() => { setAddingItemPhase({ n: phase.n, title: phase.title, parentId: item.id }); setAddingItemValue('') }}
                  style={{ background: 'none', border: 'none', padding: '1px 4px', cursor: 'pointer', fontSize: 10, color: '#3b82f6', borderRadius: 3 }}>
                  + Sub
                </button>
              )}
              <button onClick={() => handleDeleteItem(item.id)}
                style={{ background: 'none', border: 'none', padding: '1px 4px', cursor: 'pointer', fontSize: 10, color: '#ef4444', borderRadius: 3 }}>
                ✕
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              {/* Notes button */}
              <button
                onClick={(e) => { e.stopPropagation(); setItemPanel({ id: item.id, mode: 'notes' }) }}
                title={item.notes ? 'View/edit notes' : 'Add notes'}
                style={{ background: 'none', border: 'none', padding: '1px 3px', cursor: 'pointer', borderRadius: 3, lineHeight: 0 }}
                className={item.notes ? 'text-amber-400' : 'opacity-0 group-hover/item:opacity-60 text-[var(--text-muted)]'}
              >
                <StickyNote size={10} />
              </button>
              {/* Tutorial button — only if tutorial exists */}
              {item.tutorial && (
                <button
                  onClick={(e) => { e.stopPropagation(); setItemPanel({ id: item.id, mode: 'tutorial' }) }}
                  title="View tutorial"
                  style={{ background: 'none', border: 'none', padding: '1px 3px', cursor: 'pointer', borderRadius: 3, lineHeight: 0, color: '#3b82f6' }}
                >
                  <BookOpen size={10} />
                </button>
              )}
              {/* Status / time area */}
              {isSubitem ? (
                <span style={{ color: 'var(--text-muted)', fontSize: 10.5, fontVariantNumeric: 'tabular-nums', minWidth: 48, textAlign: 'right' }}>
                  {item.startedAt && !isDone ? duration(item.startedAt) : relTime(item.updatedAt)}
                </span>
              ) : hasSubitems ? (
                <span style={{ background: derivedC!.bg, color: derivedC!.text, fontSize: 10.5, fontWeight: 600, padding: '1px 7px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                  {derived!.label}
                </span>
              ) : item.status === 'blocked' ? (
                <div className="flex items-center gap-1">
                  <StatusPill status="blocked" itemType={item.type} />
                  <BlockedByBadge item={item} onUpdate={handleUpdateBlockedBy} />
                  {item.blockedSince && (
                    <span style={{ fontSize: 10, color: '#a855f7', fontVariantNumeric: 'tabular-nums' }}>
                      {duration(item.blockedSince)}
                    </span>
                  )}
                </div>
              ) : (
                <div ref={ref as React.RefObject<HTMLDivElement>} style={{ position: 'relative' }}>
                  <div className="flex items-center gap-1">
                    {item.startedAt && !isDone && (
                      <span style={{ fontSize: 10, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                        {duration(item.startedAt)}
                      </span>
                    )}
                    <button onClick={() => setOpenItem(isOpen ? null : item.id)}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                      <StatusPill status={item.status} itemType={item.type} />
                    </button>
                  </div>
                  {isOpen && (
                    <StatusDropdown item={item} onUpdate={handleStatusUpdate} onClose={() => setOpenItem(null)} anchorRef={ref} />
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sub-items */}
        {item.subitems?.map((sub) => renderItem(sub, phase, true))}

        {/* Add sub-item input */}
        {editMode && addingItemPhase?.parentId === item.id && (
          <AddItemInput
            placeholder="Sub-item name…"
            value={addingItemValue}
            onChange={setAddingItemValue}
            onConfirm={handleAddItem}
            onCancel={() => setAddingItemPhase(null)}
            indent={32}
          />
        )}
      </div>
    )
  }

  const panelItem = itemPanel
    ? (() => {
        for (const ph of project.phases) {
          for (const it of ph.items) {
            if (it.id === itemPanel.id) return it
            const sub = it.subitems?.find((s) => s.id === itemPanel.id)
            if (sub) return sub
          }
        }
        return null
      })()
    : null

  return (
    <div className="space-y-4">
      {/* Notes / tutorial modal */}
      {itemPanel && panelItem && (
        <ItemPanelModal
          item={panelItem}
          mode={itemPanel.mode}
          canEdit={itemPanel.mode === 'notes' || editMode}
          onClose={() => setItemPanel(null)}
          onSave={async (value) => {
            if (itemPanel.mode === 'notes') await handleSaveNotes(itemPanel.id, value)
            else await handleSaveTutorial(itemPanel.id, value)
          }}
        />
      )}
      {/* Back + header */}
      <div className="flex items-start gap-3">
        <button
          onClick={onBack}
          className="mt-0.5 p-1.5 rounded-md hover:bg-[var(--bg-primary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors bg-transparent border-none cursor-pointer"
        >
          <ArrowLeft size={15} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-base font-semibold text-[var(--text-primary)]">{project.customerName}</h2>
            <span style={stageBadgeStyle(project.stage)}>{project.stage}</span>
            <span className="text-xs text-[var(--text-muted)]">{project.region}</span>
          </div>
          <div className="flex items-center gap-4 mt-1 text-xs text-[var(--text-muted)]">
            <span>Owner: <span className="text-[var(--text-primary)]">{project.owner}</span></span>
            <span>{daysInStage(project.stageEnteredAt)}d in current stage</span>
            {project.targetGoLive && <span>Target: <span className="text-[var(--text-primary)]">{project.targetGoLive}</span></span>}
            <span className="ml-auto">{overall.done}/{overall.total} items done</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => { setEditMode((v) => !v); setOpenItem(null) }}
            className={`text-xs px-2.5 py-1 rounded-md border transition-colors cursor-pointer bg-transparent ${
              editMode
                ? 'border-primary/40 text-primary bg-primary/10'
                : 'border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-primary)]'
            }`}
          >
            {editMode ? '✓ Editing' : 'Edit'}
          </button>
          {!editMode && (
            <button
              onClick={() => setHideComplete((v) => !v)}
              className={`text-xs px-2.5 py-1 rounded-md border transition-colors cursor-pointer bg-transparent ${
                hideComplete
                  ? 'border-primary/40 text-primary bg-primary/10'
                  : 'border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-primary)]'
              }`}
            >
              {hideComplete ? '✓ Hiding done' : 'Hide done'}
            </button>
          )}
          <button
            onClick={refresh}
            disabled={loading}
            className="p-1.5 rounded-md hover:bg-[var(--bg-primary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors bg-transparent border-none cursor-pointer"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          </button>
          {confirmDelete ? (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-red-400">Delete project?</span>
              <button onClick={handleDelete} disabled={deleting} className="text-xs px-2 py-1 rounded-md bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 transition-colors cursor-pointer">
                {deleting ? 'Deleting…' : 'Yes, delete'}
              </button>
              <button onClick={() => setConfirmDelete(false)} className="text-xs px-2 py-1 rounded-md border border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors cursor-pointer bg-transparent">
                Cancel
              </button>
            </div>
          ) : (
            <button onClick={() => setConfirmDelete(true)} className="p-1.5 rounded-md hover:bg-red-500/10 text-[var(--text-muted)] hover:text-red-400 transition-colors bg-transparent border-none cursor-pointer" title="Delete project">
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Stage stepper */}
      <Card className="p-4 overflow-x-auto">
        <StageStepper stage={project.stage} projectId={project.id} onStageChange={handleStageChange} />
      </Card>

      {/* Phase items */}
      <Card className="overflow-hidden">
        {project.phases.map((phase, pi) => {
          const pr = phaseProgress(phase.items)
          return (
            <div key={phase.n}>
              {/* Phase header */}
              <div
                className="group/phase"
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 14px',
                  background: 'var(--bg-primary)',
                  borderTop: pi > 0 ? '1px solid var(--border-color)' : 'none',
                  borderBottom: '1px solid var(--border-color)',
                }}
              >
                <span style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: 0.6 }}>
                  PHASE {phase.n}
                </span>
                {renamingPhaseN === phase.n ? (
                  <input
                    autoFocus
                    defaultValue={phase.title}
                    onBlur={(e) => handleRenamePhase(phase.n, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleRenamePhase(phase.n, e.currentTarget.value)
                      if (e.key === 'Escape') setRenamingPhaseN(null)
                    }}
                    style={{
                      flex: 1, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
                      borderRadius: 4, padding: '1px 6px', fontSize: 12, fontWeight: 600,
                      color: 'var(--text-primary)', outline: 'none',
                    }}
                  />
                ) : (
                  <span
                    style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', flex: 1 }}
                    onDoubleClick={editMode ? () => setRenamingPhaseN(phase.n) : undefined}
                  >
                    {phase.title}
                  </span>
                )}
                {editMode ? (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setRenamingPhaseN(phase.n)}
                      style={{ background: 'none', border: 'none', padding: '1px 5px', cursor: 'pointer', fontSize: 10, color: 'var(--text-muted)', borderRadius: 3 }}
                    >Rename</button>
                    <button
                      onClick={() => { setAddingItemPhase({ n: phase.n, title: phase.title }); setAddingItemValue('') }}
                      style={{ background: 'none', border: 'none', padding: '1px 5px', cursor: 'pointer', fontSize: 10, color: '#3b82f6', borderRadius: 3 }}
                    >+ Item</button>
                    <button
                      onClick={() => handleDeletePhase(phase.n)}
                      style={{ background: 'none', border: 'none', padding: '1px 5px', cursor: 'pointer', fontSize: 10, color: '#ef4444', borderRadius: 3 }}
                    >Delete phase</button>
                  </div>
                ) : (
                  <MiniProgress {...pr} />
                )}
              </div>

              {/* Items */}
              {phase.items.map((item) => renderItem(item, phase))}

              {/* Add item input (top-level, no parent) */}
              {editMode && addingItemPhase?.n === phase.n && !addingItemPhase.parentId && (
                <AddItemInput
                  placeholder="Item name…"
                  value={addingItemValue}
                  onChange={setAddingItemValue}
                  onConfirm={handleAddItem}
                  onCancel={() => setAddingItemPhase(null)}
                  indent={14}
                />
              )}
            </div>
          )
        })}

        {/* Add phase */}
        {editMode && (
          <div style={{ borderTop: '1px solid var(--border-color)', padding: '8px 14px' }}>
            {addingPhase ? (
              <AddItemInput
                placeholder="Phase name…"
                value={addingPhaseValue}
                onChange={setAddingPhaseValue}
                onConfirm={handleAddPhase}
                onCancel={() => setAddingPhase(false)}
                indent={0}
              />
            ) : (
              <button
                onClick={() => { setAddingPhase(true); setAddingPhaseValue('') }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#3b82f6', padding: 0 }}
              >
                + Add Phase
              </button>
            )}
          </div>
        )}
      </Card>
    </div>
  )
}

// ── List view ─────────────────────────────────────────────────────────────────

function ProjectList({
  projects,
  onSelect,
  onNew,
  onDelete,
  loading,
  onRefresh,
}: {
  projects: ClientProject[]
  onSelect: (id: number) => void
  onNew: () => void
  onDelete: (id: number) => void
  loading: boolean
  onRefresh: () => void
}) {
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-[var(--text-muted)]">{projects.length} project{projects.length !== 1 ? 's' : ''}</span>
        <div className="flex items-center gap-2">
          <button
            onClick={onRefresh}
            disabled={loading}
            className="p-1.5 rounded-md hover:bg-[var(--bg-primary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors bg-transparent border-none cursor-pointer"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          </button>
          <Button variant="primary" size="sm" onClick={onNew}>
            <Plus size={13} className="mr-1" /> New Project
          </Button>
        </div>
      </div>

      {loading && projects.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
          <span className="ml-3 text-sm text-[var(--text-muted)]">Loading projects…</span>
        </div>
      ) : projects.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="text-3xl mb-3">📋</div>
          <p className="text-sm font-semibold text-[var(--text-primary)] mb-1">No client projects yet</p>
          <p className="text-xs text-[var(--text-muted)] mb-4">Create your first project to start tracking implementation progress.</p>
          <Button variant="primary" size="sm" onClick={onNew}>
            <Plus size={13} className="mr-1" /> Create First Project
          </Button>
        </div>
      ) : (
        <Card className="overflow-hidden">
          {/* Column headers */}
          <div
            style={{
              display: 'grid', gridTemplateColumns: '1fr 120px 110px 90px 80px 130px 100px',
              gap: 12, padding: '7px 14px',
              fontSize: 10.5, fontWeight: 600, letterSpacing: 0.5,
              color: 'var(--text-muted)', textTransform: 'uppercase',
              borderBottom: '1px solid var(--border-color)',
              background: 'var(--bg-primary)',
            }}
          >
            <span>Customer</span>
            <span>Owner</span>
            <span>Stage</span>
            <span>Days in stage</span>
            <span>Go-Live</span>
            <span>Progress</span>
            <span />
          </div>

          {projects.map((p, idx) => {
            const days = daysInStage(p.stageEnteredAt)
            return (
              <div
                key={p.id}
                className="group"
                onClick={() => onSelect(p.id)}
                style={{
                  display: 'grid', gridTemplateColumns: '1fr 120px 110px 90px 80px 130px 100px',
                  gap: 12, padding: '0 14px', height: 38, alignItems: 'center',
                  borderBottom: idx < projects.length - 1 ? '1px solid var(--bg-primary)' : 'none',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-primary)' }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = '' }}
              >
                <div style={{ overflow: 'hidden' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                    {p.customerName}
                  </span>
                </div>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.owner}
                </span>
                <span style={stageBadgeStyle(p.stage)}>{p.stage}</span>
                <span style={{ fontSize: 12, color: days >= 30 ? '#f59e0b' : 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                  {days}d
                </span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                  {p.targetGoLive ?? <span style={{ opacity: 0.4 }}>—</span>}
                </span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</span>
                {confirmDeleteId === p.id ? (
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => { e.stopPropagation(); onDelete(p.id); setConfirmDeleteId(null) }}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 cursor-pointer border-none"
                    >
                      Delete
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null) }}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-transparent border border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-primary)] cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(p.id) }}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-500/15 text-[var(--text-muted)] hover:text-red-400 transition-all bg-transparent border-none cursor-pointer"
                    title="Delete project"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            )
          })}
        </Card>
      )}
    </div>
  )
}

// ── Project Progress Table ────────────────────────────────────────────────────

function formatProgressDate(iso: string): string {
  const d = new Date(iso + 'Z')
  const day = d.getDate()
  const month = d.toLocaleDateString('en-AU', { month: 'short' })
  const year = String(d.getFullYear()).slice(2)
  return `${day} ${month} '${year}`
}

function ProgressCell({
  entries,
  type,
  projectId,
  onAdd,
  onDelete,
  onToggleDone,
  onDateChange,
  onTextChange,
}: {
  entries: ProjectProgressEntry[]
  type: 'update' | 'todo'
  projectId: number
  onAdd: (projectId: number, type: 'update' | 'todo', text: string) => Promise<void>
  onDelete: (entryId: number) => Promise<void>
  onToggleDone: (entryId: number, done: boolean) => Promise<void>
  onDateChange: (entryId: number, isoDate: string) => Promise<void>
  onTextChange: (entryId: number, text: string) => Promise<void>
}) {
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [editingDateId, setEditingDateId] = useState<number | null>(null)
  const [editingTextId, setEditingTextId] = useState<number | null>(null)
  const [textDraft, setTextDraft] = useState('')
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const priorityKey = `progress-priority-${projectId}-${type}`
  const [priorityIds, setPriorityIds] = useState<Set<number>>(() => {
    try { const s = localStorage.getItem(priorityKey); return s ? new Set(JSON.parse(s) as number[]) : new Set() } catch { return new Set() }
  })
  function togglePriority(id: number) {
    setPriorityIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      try { localStorage.setItem(priorityKey, JSON.stringify([...next])) } catch { /* ignore */ }
      return next
    })
  }

  const handleAdd = async () => {
    if (!draft.trim()) return
    setSaving(true)
    await onAdd(projectId, type, draft.trim())
    setDraft('')
    setSaving(false)
    setAdding(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.shiftKey || e.metaKey)) { e.preventDefault(); void handleAdd() }
    if (e.key === 'Escape') { setAdding(false); setDraft('') }
  }

  useEffect(() => {
    if (adding) setTimeout(() => inputRef.current?.focus(), 50)
  }, [adding])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
      {entries.map((entry) => (
        <div
          key={entry.id}
          className="group/entry"
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 4, minWidth: 0,
            ...(type === 'todo' && priorityIds.has(entry.id) ? {
              background: 'rgba(239,68,68,0.07)', borderRadius: 4, padding: '2px 4px', margin: '0 -4px',
              outline: '1px solid rgba(239,68,68,0.3)',
            } : {}),
          }}
        >
          {type === 'todo' && (
            <>
              <button
                onClick={() => void onToggleDone(entry.id, !entry.done)}
                style={{
                  flexShrink: 0, width: 14, height: 14, marginTop: 1, borderRadius: 3,
                  border: `1.5px solid ${entry.done ? '#22c55e' : 'var(--border-color)'}`,
                  background: entry.done ? '#22c55e' : 'transparent',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: 0,
                }}
              >
                {entry.done && <Check size={9} color="#fff" strokeWidth={3} />}
              </button>
              <button
                onClick={() => togglePriority(entry.id)}
                title={priorityIds.has(entry.id) ? 'Remove priority' : 'Mark as priority'}
                style={{
                  flexShrink: 0, background: 'transparent', border: 'none', cursor: 'pointer',
                  padding: '0 1px', lineHeight: 1, marginTop: 1,
                  opacity: priorityIds.has(entry.id) ? 1 : 0, transition: 'opacity 0.15s',
                }}
                className="group-hover/entry:!opacity-100"
              >
                <Flag size={11} color={priorityIds.has(entry.id) ? '#ef4444' : 'var(--text-muted)'} fill={priorityIds.has(entry.id) ? '#ef4444' : 'none'} />
              </button>
            </>
          )}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 6 }}>
            {/* Date — click to edit */}
            {editingDateId === entry.id ? (
              <input
                type="datetime-local"
                defaultValue={entry.created_at.replace(' ', 'T').slice(0, 16)}
                autoFocus
                onBlur={(e) => {
                  const val = e.target.value
                  if (val) {
                    const d = new Date(val)
                    const utc = d.toISOString().replace('T', ' ').slice(0, 19)
                    void onDateChange(entry.id, utc)
                  }
                  setEditingDateId(null)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setEditingDateId(null)
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                }}
                style={{
                  fontSize: 10, padding: '1px 4px', borderRadius: 4, flexShrink: 0,
                  border: '1px solid var(--border-color)', background: 'var(--bg-primary)',
                  color: 'var(--text-primary)', outline: 'none',
                }}
              />
            ) : (
              <button
                onClick={() => setEditingDateId(entry.id)}
                title="Click to change date"
                style={{
                  flexShrink: 0, fontSize: 9.5, fontWeight: 600, color: '#3b82f6',
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  padding: 0, whiteSpace: 'nowrap', lineHeight: 1.4,
                }}
                className="hover:text-[var(--text-primary)]"
              >
                {formatProgressDate(entry.created_at)}
              </button>
            )}
            {/* Text — double-click to edit */}
            {editingTextId === entry.id ? (
              <textarea
                value={textDraft}
                onChange={(e) => { setTextDraft(e.target.value); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px' }}
                autoFocus
                rows={4}
                onBlur={async () => {
                  if (textDraft.trim() && textDraft.trim() !== entry.text) {
                    await onTextChange(entry.id, textDraft.trim())
                  }
                  setEditingTextId(null)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.shiftKey || e.metaKey)) { e.preventDefault(); (e.target as HTMLTextAreaElement).blur() }
                  if (e.key === 'Escape') { setEditingTextId(null) }
                }}
                style={{
                  fontSize: 11.5, padding: '4px 6px', borderRadius: 4, flex: 1, minWidth: 0,
                  border: '1px solid var(--border-color)', background: 'var(--bg-primary)',
                  color: 'var(--text-primary)', outline: 'none', resize: 'none', lineHeight: 1.5,
                  overflow: 'hidden', minHeight: 64,
                }}
              />
            ) : (
              <span
                onDoubleClick={() => { setEditingTextId(entry.id); setTextDraft(entry.text) }}
                title="Double-click to edit"
                style={{
                  fontSize: 11.5, color: entry.done ? 'var(--text-muted)' : 'var(--text-primary)',
                  textDecoration: entry.done ? 'line-through' : 'none',
                  wordBreak: 'break-word', lineHeight: 1.4, cursor: 'text', whiteSpace: 'pre-wrap',
                }}
              >
                {entry.text}
              </span>
            )}
          </div>
          <button
            onClick={() => void onDelete(entry.id)}
            className="opacity-0 group-hover/entry:opacity-100"
            style={{
              flexShrink: 0, background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'var(--text-muted)', padding: '1px 2px', borderRadius: 3,
              lineHeight: 1, transition: 'opacity 0.15s',
            }}
            title="Remove"
          >
            <X size={10} />
          </button>
        </div>
      ))}

      {adding ? (
        <div style={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
          <textarea
            ref={inputRef}
            value={draft}
            rows={4}
            onChange={(e) => { setDraft(e.target.value); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px' }}
            onKeyDown={handleKeyDown}
            placeholder={type === 'update' ? 'Add update… (Shift+Enter to save)' : 'Add to-do… (Shift+Enter to save)'}
            disabled={saving}
            style={{
              flex: 1, fontSize: 11.5, padding: '4px 6px', borderRadius: 4, minWidth: 0,
              border: '1px solid var(--border-color)', background: 'var(--bg-primary)',
              color: 'var(--text-primary)', outline: 'none', resize: 'none', lineHeight: 1.5,
              overflow: 'hidden', minHeight: 64,
            }}
          />
          <button
            onClick={() => void handleAdd()}
            disabled={saving || !draft.trim()}
            style={{
              fontSize: 10, padding: '2px 6px', borderRadius: 4, cursor: 'pointer',
              background: '#3b82f6', color: '#fff', border: 'none',
              opacity: saving || !draft.trim() ? 0.5 : 1,
            }}
          >
            Add
          </button>
          <button
            onClick={() => { setAdding(false); setDraft('') }}
            style={{
              fontSize: 10, padding: '2px 4px', borderRadius: 4, cursor: 'pointer',
              background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border-color)',
            }}
          >
            <X size={10} />
          </button>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          style={{
            fontSize: 10.5, color: 'var(--text-muted)', background: 'transparent', border: 'none',
            cursor: 'pointer', padding: '1px 0', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 3,
          }}
          className="hover:text-[var(--text-primary)]"
        >
          <Plus size={10} /> add item
        </button>
      )}
    </div>
  )
}

function ProjectProgressTable({
  projects,
  allProgress,
  onProjectClick,
  onAdd,
  onDelete,
  onToggleDone,
  onDateChange,
  onTextChange,
}: {
  projects: ClientProject[]
  allProgress: ProjectProgressEntry[]
  onProjectClick: (id: number) => void
  onAdd: (projectId: number, type: 'update' | 'todo', text: string) => Promise<void>
  onDelete: (entryId: number) => Promise<void>
  onToggleDone: (entryId: number, done: boolean) => Promise<void>
  onDateChange: (entryId: number, isoDate: string) => Promise<void>
  onTextChange: (entryId: number, text: string) => Promise<void>
}) {
  const activeProjects = projects.filter((p) => !p.archived)

  if (activeProjects.length === 0) return null

  const getEntries = (projectId: number, type: 'update' | 'todo') =>
    allProgress.filter((e) => e.project_id === projectId && e.type === type)

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
        padding: '10px 14px', borderRadius: 8,
        background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
      }}>
        <div style={{
          width: 28, height: 28, borderRadius: 6, background: 'rgba(59,130,246,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <StickyNote size={14} color="#3b82f6" />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Project Progress</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Track updates and to-dos per project</div>
        </div>
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        {/* Column headers */}
        <div style={{
          display: 'grid', gridTemplateColumns: '160px 1fr 1fr',
          gap: 0, padding: '6px 14px',
          fontSize: 10.5, fontWeight: 600, letterSpacing: 0.5,
          color: 'var(--text-muted)', textTransform: 'uppercase',
          borderBottom: '1px solid var(--border-color)',
          background: 'var(--bg-primary)',
        }}>
          <span>Project</span>
          <span style={{ paddingLeft: 12 }}>Updates</span>
          <span style={{ paddingLeft: 12 }}>To Do</span>
        </div>

        {activeProjects.map((project, idx) => (
          <div
            key={project.id}
            style={{
              display: 'grid', gridTemplateColumns: '160px 1fr 1fr',
              borderBottom: idx < activeProjects.length - 1 ? '1px solid var(--border-color)' : 'none',
              alignItems: 'stretch',
            }}
          >
            {/* Project name */}
            <div style={{
              padding: '10px 14px', borderRight: '1px solid var(--border-color)',
              display: 'flex', alignItems: 'flex-start', paddingTop: 12,
            }}>
              <button
                onClick={() => onProjectClick(project.id)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                  fontSize: 12.5, fontWeight: 600, color: '#3b82f6', textAlign: 'left',
                  lineHeight: 1.3, wordBreak: 'break-word',
                }}
                className="hover:underline"
              >
                {project.customerName}
              </button>
            </div>

            {/* Updates */}
            <div style={{ padding: '10px 12px', borderRight: '1px solid var(--border-color)' }}>
              <ProgressCell
                entries={getEntries(project.id, 'update')}
                type="update"
                projectId={project.id}
                onAdd={onAdd}
                onDelete={onDelete}
                onToggleDone={onToggleDone}
                onDateChange={onDateChange}
                onTextChange={onTextChange}
              />
            </div>

            {/* To Do */}
            <div style={{ padding: '10px 12px' }}>
              <ProgressCell
                entries={getEntries(project.id, 'todo')}
                type="todo"
                projectId={project.id}
                onAdd={onAdd}
                onDelete={onDelete}
                onToggleDone={onToggleDone}
                onDateChange={onDateChange}
                onTextChange={onTextChange}
              />
            </div>
          </div>
        ))}
      </Card>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function ClientProjectsPage() {
  const [projects, setProjects] = useState<ClientProject[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedProject, setSelectedProject] = useState<ClientProjectDetail | null>(null)
  const [showNewModal, setShowNewModal] = useState(false)
  const [allProgress, setAllProgress] = useState<ProjectProgressEntry[]>([])

  const fetchList = useCallback(async () => {
    setLoading(true)
    const [projectsRes, progressRes] = await Promise.all([
      clientProjectsApi.list(),
      clientProjectsApi.listProgress(),
    ])
    if (projectsRes.success && projectsRes.data) setProjects(projectsRes.data)
    if (progressRes.success && progressRes.data) setAllProgress(progressRes.data)
    setLoading(false)
  }, [])

  useEffect(() => { void fetchList() }, [fetchList])

  const handleProgressAdd = async (projectId: number, type: 'update' | 'todo', text: string) => {
    const res = await clientProjectsApi.addProgress(projectId, type, text)
    if (res.success && res.data) {
      setAllProgress((prev) => [...prev, res.data as ProjectProgressEntry])
    }
  }

  const handleProgressDelete = async (entryId: number) => {
    await clientProjectsApi.deleteProgress(entryId)
    setAllProgress((prev) => prev.filter((e) => e.id !== entryId))
  }

  const handleProgressToggle = async (entryId: number, done: boolean) => {
    await clientProjectsApi.patchProgress(entryId, { done })
    setAllProgress((prev) => prev.map((e) => e.id === entryId ? { ...e, done } : e))
  }

  const handleProgressDateChange = async (entryId: number, isoDate: string) => {
    await clientProjectsApi.patchProgress(entryId, { created_at: isoDate })
    setAllProgress((prev) =>
      prev.map((e) => e.id === entryId ? { ...e, created_at: isoDate } : e)
        .sort((a, b) => a.created_at.localeCompare(b.created_at))
    )
  }

  const handleProgressTextChange = async (entryId: number, text: string) => {
    await clientProjectsApi.patchProgress(entryId, { text })
    setAllProgress((prev) => prev.map((e) => e.id === entryId ? { ...e, text } : e))
  }

  const handleSelect = async (id: number) => {
    const res = await clientProjectsApi.get(id)
    if (res.success && res.data) setSelectedProject(res.data)
  }

  const handleCreated = (project: ClientProject) => {
    setProjects((prev) => [project, ...prev])
    // If ClientProjectDetail was returned, open it
    if ('phases' in project) setSelectedProject(project as ClientProjectDetail)
  }

  const handleProjectUpdated = (updated: ClientProjectDetail) => {
    setProjects((prev) => prev.map((p) => p.id === updated.id ? updated : p))
    setSelectedProject(updated)
  }

  const handleDelete = async (id: number) => {
    await clientProjectsApi.remove(id)
    setProjects((prev) => prev.filter((p) => p.id !== id))
    if (selectedProject?.id === id) setSelectedProject(null)
  }

  return (
    <div>
      {showNewModal && (
        <NewProjectModal
          onClose={() => setShowNewModal(false)}
          onCreate={handleCreated}
        />
      )}

      {selectedProject ? (
        <ProjectDetail
          project={selectedProject}
          onBack={() => setSelectedProject(null)}
          onProjectUpdated={handleProjectUpdated}
          onDelete={handleDelete}
        />
      ) : (
        <>
          <ProjectProgressTable
            projects={projects}
            allProgress={allProgress}
            onProjectClick={handleSelect}
            onAdd={handleProgressAdd}
            onDelete={handleProgressDelete}
            onToggleDone={handleProgressToggle}
            onDateChange={handleProgressDateChange}
            onTextChange={handleProgressTextChange}
          />
          <ProjectList
            projects={projects}
            onSelect={handleSelect}
            onNew={() => setShowNewModal(true)}
            onDelete={handleDelete}
            loading={loading}
            onRefresh={fetchList}
          />
        </>
      )}
    </div>
  )
}
